import { useRef, useState } from 'react';
import styles from './Contact.module.css';
import { site } from '../content/site.js';

const FORMSPREE_ID = import.meta.env.VITE_FORMSPREE_ID || '';

export default function Contact() {
  const c = site.contact;
  const [values, setValues] = useState({ name: '', email: '', type: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const formRef = useRef(null);

  const onChange = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: '' }));
  };

  const validate = () => {
    const er = {};
    if (!values.name.trim()) er.name = 'Required';
    if (!values.email.trim()) er.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) er.email = 'Invalid email';
    if (!values.type) er.type = 'Pick one';
    if (!values.message.trim() || values.message.trim().length < 10)
      er.message = 'Min 10 chars';
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    const firstInvalid = Object.keys(er)[0];
    if (firstInvalid) {
      formRef.current?.querySelector(`#${firstInvalid}`)?.focus();
      return;
    }

    setStatus('submitting');
    try {
      if (!FORMSPREE_ID) {
        if (import.meta.env.DEV) {
          // Local dev without Formspree: fake a successful send
          await new Promise((r) => setTimeout(r, 700));
          setStatus('success');
        } else {
          // Never pretend a message was sent when it wasn't
          console.error('VITE_FORMSPREE_ID is not set; contact form cannot send.');
          setStatus('error');
        }
        return;
      }
      const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...values, _gotcha: formRef.current?.elements._gotcha?.value || '' }),
      });
      if (res.ok) setStatus('success');
      else setStatus('error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className={styles.contact} id="contact">
      <header className="section-head">
        <span className="num" aria-hidden="true">{c.num}</span>
        <h2>{c.title}</h2>
        <p className="meta">{c.meta}</p>
      </header>

      <div className={styles.wrap}>
        <h3 className={styles.headline}>{c.headline}</h3>
        <div className={styles.details}>
          {c.details.map((d, i) => (
            <div key={i}>
              {typeof d === 'object'
                ? <>{d.label}<a href={d.href} target="_blank" rel="noopener noreferrer">{d.link}</a></>
                : d || '\u00A0'}
            </div>
          ))}
        </div>

        {status === 'success' ? (
          <div className={styles.success} role="status">
            Message received — I'll reply within one business day.
          </div>
        ) : (
          <form className={styles.form} onSubmit={onSubmit} noValidate ref={formRef}>
            {/* Honeypot: hidden from people, filled by bots; Formspree drops those submissions */}
            <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" aria-hidden="true" className="sr-only" />

            <div className={styles.field}>
              <label className={styles.label} htmlFor="name">Name</label>
              <input
                id="name"
                name="name"
                autoComplete="name"
                className={styles.input}
                value={values.name}
                onChange={onChange('name')}
                aria-invalid={!!errors.name}
                aria-describedby="name-err"
              />
              <div id="name-err" className={styles.error} aria-live="polite">{errors.name}</div>
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                autoComplete="email"
                type="email"
                className={styles.input}
                value={values.email}
                onChange={onChange('email')}
                aria-invalid={!!errors.email}
                aria-describedby="email-err"
              />
              <div id="email-err" className={styles.error} aria-live="polite">{errors.email}</div>
            </div>

            <div className={`${styles.field} ${styles.full}`}>
              <label className={styles.label} htmlFor="type">Project type</label>
              <select
                id="type"
                name="type"
                className={styles.select}
                value={values.type}
                onChange={onChange('type')}
                aria-invalid={!!errors.type}
                aria-describedby="type-err"
              >
                <option value="">Select —</option>
                {c.projectTypes.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
              <div id="type-err" className={styles.error} aria-live="polite">{errors.type}</div>
            </div>

            <div className={`${styles.field} ${styles.full}`}>
              <label className={styles.label} htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                className={styles.textarea}
                value={values.message}
                onChange={onChange('message')}
                aria-invalid={!!errors.message}
                aria-describedby="message-err"
              />
              <div id="message-err" className={styles.error} aria-live="polite">{errors.message}</div>
            </div>

            <div className={styles.submit}>
              <span className={styles.note} role="status">
                {status === 'error' ? 'Something went wrong — try again, or message me on LinkedIn' : 'Protected by goodwill'}
              </span>
              <button className={styles.btn} type="submit" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Sending…' : 'Send →'}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
